<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, onMounted } from 'vue';
import nexceoSettingsAPI from 'dashboard/api/nexceo/settings';
/* global axios */

const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref(null);
const saveError = ref(null);
const saveSuccess = ref(false);

const envAiEnabled = ref(true);
const effectiveAiEnabled = ref(true);
const whatsappInboxes = ref([]);
const currentAccountId =
  window.chatwootConfig?.accountId ||
  window.location.pathname.match(/accounts\/(\d+)/)?.[1];

// Daily report recipient text inputs (comma-separated strings bound to arrays in settings)
const whatsappNumbersInput = ref('');
const reportEmailsInput = ref('');

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const PROVIDERS = ['openai', 'anthropic'];
const PROVIDER_MODELS = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: [
    'claude-opus-4-7',
    'claude-sonnet-4-6',
    'claude-haiku-4-5-20251001',
  ],
};
const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];
const DEFAULT_WORKING_HOURS = {
  monday: { open: '09:00', close: '18:00', closed: false },
  tuesday: { open: '09:00', close: '18:00', closed: false },
  wednesday: { open: '09:00', close: '18:00', closed: false },
  thursday: { open: '09:00', close: '18:00', closed: false },
  friday: { open: '09:00', close: '18:00', closed: false },
  saturday: { open: '10:00', close: '16:00', closed: false },
  sunday: { open: '09:00', close: '18:00', closed: true },
};

const settings = reactive({
  global_ai_enabled: true,
  auto_reply_enabled: true,
  human_handoff_enabled: true,
  approval_queue_enabled: true,
  follow_up_enabled: true,
  site_visit_reminders_enabled: true,
  max_follow_ups_per_lead: 3,
  follow_up_cooldown_hours: 24,
  pending_action_expiry_hours: 48,
  working_hours_enabled: false,
  timezone: 'Asia/Kolkata',
  working_hours: JSON.parse(JSON.stringify(DEFAULT_WORKING_HOURS)),
  default_ai_provider: 'openai',
  default_ai_model: '',
  fallback_ai_provider: '',
  fallback_ai_model: '',
  max_ai_replies_per_conversation: 20,
  daily_ai_cost_limit: '',
  monthly_ai_cost_limit: '',
  // Notification delivery settings (Milestone 17 / 17.1)
  agent_notifications_enabled: true,
  notification_private_note_enabled: true,
  notification_email_enabled: false,
  notification_webhook_enabled: false,
  notification_webhook_url: '',
  // Chatwoot WhatsApp internal-alert channel (17.1)
  notification_chatwoot_whatsapp_enabled: false,
  notification_chatwoot_whatsapp_inbox_id: null,
  notification_chatwoot_whatsapp_template_mode: false,
  notification_chatwoot_whatsapp_default_template: '',
  // Canonical event names
  notification_events_enabled: {
    new_lead: true,
    hot_lead: true,
    site_visit_scheduled: true,
    human_handoff: true,
    approval_required: true,
    booking_intent: true,
    ai_confidence_low: false,
  },
  notification_retry_enabled: true,
  notification_max_attempts: 3,
  // Legacy
  whatsapp_notifications_enabled: false,
  notification_provider: 'private_note',
  // Admin Daily Report
  daily_admin_report_enabled: false,
  daily_admin_report_time: '20:00',
  daily_admin_report_timezone: 'Asia/Kolkata',
  daily_admin_report_whatsapp_numbers: [],
  daily_admin_report_emails: [],
  daily_admin_report_whatsapp_enabled: true,
  daily_admin_report_email_enabled: true,
  daily_admin_report_template_name: '',
  daily_admin_report_include_projects: true,
  daily_admin_report_include_agents: true,
  daily_admin_report_include_ai_summary: true,
  daily_admin_report_include_notifications: true,
  // Company Knowledge (Milestone 19)
  company_name: '',
  company_about: '',
  company_tagline: '',
  office_address: '',
  contact_phone: '',
  contact_email: '',
  website_url: '',
  brand_tone: 'professional and warm',
  why_choose_us: '',
  company_faqs: [],
  forbidden_claims: [],
  // Unified Google Integration (Calendar + Meet + Sheets)
  google_oauth_client_id: '',
  google_oauth_client_secret: '',
  // Read-only display fields returned from settings API:
  google_oauth_credentials_source: 'missing', // 'env' | 'db' | 'missing'
  google_oauth_connected: false,
  google_oauth_connected_email: '',
  google_oauth_scope: '',
  google_oauth_connected_at: '',
  // Google Calendar / Meet
  google_calendar_enabled: false,
  google_calendar_id: '',
  google_calendar_timezone: '',
  google_calendar_add_meet_for_video_calls: true,
  google_calendar_auth_mode: 'service_account',
  google_calendar_connected_email: '', // legacy — kept for save compat
  google_calendar_impersonated_user: '',
  google_calendar_credentials: '',
  // Google Sheets Sync
  google_sheets_sync_enabled: false,
  google_sheets_spreadsheet_id: '',
  google_sheets_sheet_name: 'Verified Leads',
  google_sheets_credentials: '',
  google_sheets_sync_mode: 'event_based',
  google_sheets_auth_mode: 'service_account',
});

const testNotifLoading = ref(false);
const testNotifResult = ref(null);

// Company knowledge textarea refs (arrays stored as one-per-line text)
const faqsInput = ref('');
const forbiddenClaimsInput = ref('');

// Follow-up template sync
const templateSyncLoading = ref(false);
const templateSyncResult = ref(null);

// Meeting confirmation template sync
const meetingTemplateLoading = ref(false);
const meetingTemplateResult = ref(null);

// CSV export
const exportLoading = ref('');
const exportError = ref('');

// AI Provider status (loaded from backend, shown as summary card)
const aiProviderStatus = ref(null);
const aiProviderTestLoading = ref(false);
const aiProviderTestResult = ref(null);

// WhatsApp test inline form state
const waTestFormOpen = ref(false);
const waTestRecipients = ref([]);
const waTestMemberId = ref('');
const waTestPhone = ref('');
const waTestRecipientsLoading = ref(false);

const NOTIFICATION_EVENTS = [
  { key: 'new_lead', label: 'New Lead' },
  { key: 'hot_lead', label: 'Hot Lead' },
  { key: 'site_visit_scheduled', label: 'Site Visit Scheduled' },
  { key: 'human_handoff', label: 'Human Handoff' },
  { key: 'approval_required', label: 'Approval Required' },
  { key: 'booking_intent', label: 'Booking Intent' },
  { key: 'ai_confidence_low', label: 'AI Confidence Low' },
];

const primaryModels = computed(
  () => PROVIDER_MODELS[settings.default_ai_provider] || []
);
const fallbackModels = computed(
  () => PROVIDER_MODELS[settings.fallback_ai_provider] || []
);
const aiControlsDisabled = computed(() => !envAiEnabled.value);

function applySettings(data) {
  Object.keys(settings).forEach(key => {
    if (data[key] === undefined || data[key] === null) return;
    if (key === 'working_hours' && typeof data[key] === 'object') {
      settings.working_hours = JSON.parse(
        JSON.stringify(DEFAULT_WORKING_HOURS)
      );
      Object.assign(settings.working_hours, data[key]);
    } else if (
      key === 'notification_events_enabled' &&
      typeof data[key] === 'object'
    ) {
      Object.assign(settings.notification_events_enabled, data[key]);
    } else {
      settings[key] = data[key];
    }
  });
  // Normalize null to empty string for optional fields
  [
    'daily_ai_cost_limit',
    'monthly_ai_cost_limit',
    'notification_webhook_url',
    'notification_chatwoot_whatsapp_default_template',
    'default_ai_model',
    'fallback_ai_provider',
    'fallback_ai_model',
    'daily_admin_report_template_name',
  ].forEach(k => {
    if (settings[k] === null) settings[k] = '';
  });
  // Sync array fields to text inputs
  whatsappNumbersInput.value = (
    settings.daily_admin_report_whatsapp_numbers || []
  ).join(', ');
  reportEmailsInput.value = (settings.daily_admin_report_emails || []).join(
    ', '
  );
  // Sync company knowledge arrays to textarea refs
  faqsInput.value = (settings.company_faqs || []).join('\n');
  forbiddenClaimsInput.value = (settings.forbidden_claims || []).join('\n');
  // Normalize null company knowledge strings
  [
    'company_name',
    'company_about',
    'company_tagline',
    'office_address',
    'contact_phone',
    'contact_email',
    'website_url',
    'brand_tone',
    'why_choose_us',
    'google_oauth_client_id',
    'google_oauth_client_secret',
    'google_oauth_scope',
    'google_oauth_connected_at',
    'google_oauth_connected_email',
    'google_calendar_id',
    'google_calendar_timezone',
    'google_calendar_credentials',
    'google_calendar_connected_email',
    'google_calendar_impersonated_user',
    'google_sheets_spreadsheet_id',
    'google_sheets_sheet_name',
    'google_sheets_credentials',
    'google_sheets_sync_mode',
    'google_sheets_auth_mode',
  ].forEach(k => {
    if (settings[k] === null || settings[k] === undefined) settings[k] = '';
  });
  settings.google_sheets_sheet_name =
    settings.google_sheets_sheet_name || 'Verified Leads';
  // Read-only display fields (injected by the API)
  if (data.google_oauth_credentials_source !== undefined)
    settings.google_oauth_credentials_source =
      data.google_oauth_credentials_source || 'missing';
  if (data.google_oauth_connected !== undefined)
    settings.google_oauth_connected = !!data.google_oauth_connected;
  if (data.google_oauth_connected_email !== undefined)
    settings.google_oauth_connected_email =
      data.google_oauth_connected_email || '';
}

function syncWhatsappNumbers() {
  settings.daily_admin_report_whatsapp_numbers = whatsappNumbersInput.value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function syncReportEmails() {
  settings.daily_admin_report_emails = reportEmailsInput.value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function syncFaqs() {
  settings.company_faqs = faqsInput.value
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}

function syncForbiddenClaims() {
  settings.forbidden_claims = forbiddenClaimsInput.value
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}

async function checkFollowupTemplates() {
  templateSyncLoading.value = true;
  templateSyncResult.value = null;
  try {
    const { data } = await nexceoSettingsAPI.checkFollowupTemplates();
    templateSyncResult.value = data;
  } catch (e) {
    templateSyncResult.value = {
      error: e?.response?.data?.error ?? 'Failed to check templates.',
    };
  } finally {
    templateSyncLoading.value = false;
  }
}

async function checkMeetingTemplates() {
  meetingTemplateLoading.value = true;
  meetingTemplateResult.value = null;
  try {
    const { data } = await nexceoSettingsAPI.checkMeetingTemplates();
    meetingTemplateResult.value = data;
  } catch (e) {
    meetingTemplateResult.value = {
      error: e?.response?.data?.error ?? 'Failed to check meeting templates.',
    };
  } finally {
    meetingTemplateLoading.value = false;
  }
}

async function loadWhatsappTestRecipients() {
  waTestRecipientsLoading.value = true;
  try {
    const { data } = await nexceoSettingsAPI.whatsappTestRecipients();
    waTestRecipients.value = data.recipients || [];
  } catch {
    waTestRecipients.value = [];
  } finally {
    waTestRecipientsLoading.value = false;
  }
}

function openWhatsappTestForm() {
  waTestFormOpen.value = true;
  waTestMemberId.value = '';
  waTestPhone.value = '';
  testNotifResult.value = null;
  loadWhatsappTestRecipients();
}

async function sendWhatsappTest() {
  testNotifLoading.value = true;
  testNotifResult.value = null;
  const options = {};
  if (waTestMemberId.value) {
    options.project_team_member_id = waTestMemberId.value;
  } else if (waTestPhone.value.trim()) {
    options.test_phone = waTestPhone.value.trim();
  }
  try {
    const { data } = await nexceoSettingsAPI.testNotification(
      'chatwoot_whatsapp',
      options
    );
    testNotifResult.value = {
      success: data.success,
      message: data.message,
      status: data.status,
      conversationId: data.chatwoot_conversation_id,
      contactId: data.chatwoot_contact_id,
      deliveryId: data.delivery_id,
      messageId: data.message_id,
      templateModeUsed: data.template_mode_used,
      templateName: data.template_name,
      templateLanguage: data.template_language,
      templateCategory: data.template_category,
      templateVariablesCount: data.template_variables_count,
      hasTemplateParams: data.has_template_params,
    };
    if (data.success) waTestFormOpen.value = false;
  } catch (e) {
    testNotifResult.value = {
      success: false,
      message: e?.response?.data?.error ?? 'Test failed.',
    };
  } finally {
    testNotifLoading.value = false;
    if (testNotifResult.value?.success) {
      setTimeout(() => {
        testNotifResult.value = null;
      }, 8000);
    }
  }
}

async function testNotification(channel) {
  testNotifLoading.value = true;
  testNotifResult.value = null;
  try {
    const { data } = await nexceoSettingsAPI.testNotification(channel);
    testNotifResult.value = { success: true, message: data.message };
  } catch (e) {
    testNotifResult.value = {
      success: false,
      message: e?.response?.data?.error ?? 'Test failed.',
    };
  } finally {
    testNotifLoading.value = false;
    setTimeout(() => {
      testNotifResult.value = null;
    }, 5000);
  }
}

async function load() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await nexceoSettingsAPI.get();
    applySettings(data.settings || {});
    envAiEnabled.value = data.environment?.nexceo_ai_enabled !== false;
    effectiveAiEnabled.value = data.effective?.ai_enabled !== false;
  } catch {
    loadError.value = 'Failed to load settings. Please refresh.';
  } finally {
    isLoading.value = false;
  }
}

async function save() {
  // Flush any uncommitted textarea values before saving
  syncWhatsappNumbers();
  syncReportEmails();
  syncFaqs();
  syncForbiddenClaims();
  isSaving.value = true;
  saveError.value = null;
  saveSuccess.value = false;
  try {
    const payload = {
      ...settings,
      working_hours: { ...settings.working_hours },
      notification_events_enabled: { ...settings.notification_events_enabled },
    };
    // Cost limits are not enforced yet — omit from payload to avoid misleading saves.
    delete payload.daily_ai_cost_limit;
    delete payload.monthly_ai_cost_limit;
    [
      'notification_webhook_url',
      'notification_chatwoot_whatsapp_default_template',
      'default_ai_model',
      'fallback_ai_provider',
      'fallback_ai_model',
      'company_name',
      'company_about',
      'company_tagline',
      'office_address',
      'contact_phone',
      'contact_email',
      'website_url',
      'brand_tone',
      'why_choose_us',
      'google_oauth_client_id',
      'google_oauth_scope',
      'google_oauth_connected_at',
      'google_calendar_id',
      'google_calendar_timezone',
      'google_calendar_credentials',
      'google_calendar_connected_email',
      'google_calendar_impersonated_user',
      'google_sheets_spreadsheet_id',
      'google_sheets_sheet_name',
      'google_sheets_credentials',
      'google_sheets_sync_mode',
      'google_sheets_auth_mode',
    ].forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });
    // Never send read-only computed fields back.
    delete payload.google_oauth_credentials_source;
    delete payload.google_oauth_connected;
    delete payload.google_oauth_connected_email;
    // Never overwrite the masked secret placeholder.
    if (payload.google_oauth_client_secret === '[masked]') {
      delete payload.google_oauth_client_secret;
    } else if (payload.google_oauth_client_secret === '') {
      payload.google_oauth_client_secret = null;
    }
    const { data } = await nexceoSettingsAPI.update(payload);
    applySettings(data.settings || {});
    envAiEnabled.value = data.environment?.nexceo_ai_enabled !== false;
    effectiveAiEnabled.value = data.effective?.ai_enabled !== false;
    saveSuccess.value = true;
    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch (e) {
    saveError.value = e?.response?.data?.error ?? 'Failed to save settings.';
  } finally {
    isSaving.value = false;
  }
}

function whatsappProviderLabel(inbox) {
  if (inbox.channel_type === 'Channel::TwilioSms') return 'Twilio WhatsApp';
  if (inbox.provider === 'whatsapp_cloud') return 'WhatsApp Cloud';
  return 'WhatsApp';
}

function getDay(day) {
  return settings.working_hours[day] || { ...DEFAULT_WORKING_HOURS[day] };
}
function setDayField(day, field, value) {
  if (!settings.working_hours[day]) {
    settings.working_hours[day] = { ...DEFAULT_WORKING_HOURS[day] };
  }
  settings.working_hours[day][field] = value;
}
function dayLabel(d) {
  return d.charAt(0).toUpperCase() + d.slice(1, 3);
}

async function downloadExport(type, filters = {}) {
  exportLoading.value = type;
  exportError.value = '';
  try {
    const accountId =
      window.chatwootConfig?.accountId ||
      window.location.pathname.match(/accounts\/(\d+)/)?.[1];
    const resp = await axios.get(
      `/api/v1/accounts/${accountId}/nexceo/exports/${type}`,
      { params: filters, responseType: 'blob' }
    );

    // Verify the response is actually CSV (not an error JSON wrapped in a blob)
    const contentType = resp.headers?.['content-type'] || '';
    if (
      !contentType.includes('text/csv') &&
      !contentType.includes('text/plain')
    ) {
      // Server returned JSON error but responseType:blob wraps it — parse and show real error
      const text = await resp.data.text();
      let msg = `Export failed (HTTP ${resp.status})`;
      try {
        const parsed = JSON.parse(text);
        msg = parsed.error || parsed.message || msg;
      } catch {
        msg = text.slice(0, 200) || msg;
      }
      exportError.value = msg;
      return;
    }

    const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    // When responseType:blob and server returns 4xx/5xx, e.response.data is a Blob
    let errorMsg = `Export failed for ${type}.`;
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text);
        const status = e?.response?.status;
        if (status === 403)
          errorMsg = `Access denied: ${parsed.error || 'Admin access required.'}`;
        else if (status === 404) errorMsg = `Export type not found: ${type}`;
        else
          errorMsg =
            parsed.error ||
            `Export failed (HTTP ${status}): ${text.slice(0, 100)}`;
      } catch {
        errorMsg = `Export failed for ${type}. Check the browser console for details.`;
      }
    } else if (e?.response?.data?.error) {
      errorMsg = e.response.data.error;
    }
    exportError.value = errorMsg;
  } finally {
    exportLoading.value = '';
  }
}

async function loadWhatsappInboxes() {
  try {
    const accountId =
      window.chatwootConfig?.accountId ||
      window.location.pathname.match(/accounts\/(\d+)/)?.[1];
    if (!accountId) return;
    const resp = await axios.get(`/api/v1/accounts/${accountId}/inboxes`);
    whatsappInboxes.value = (resp.data?.payload || []).filter(
      i =>
        i.channel_type === 'Channel::Whatsapp' ||
        (i.channel_type === 'Channel::TwilioSms' && i.medium === 'whatsapp')
    );
  } catch {
    // non-fatal — inbox list stays empty
  }
}

async function loadAiProviderStatus() {
  try {
    const acct = currentAccountId;
    const { data } = await axios.get(
      `/api/v1/accounts/${acct}/nexceo/settings/ai_provider_status`
    );
    aiProviderStatus.value = data;
  } catch {
    /* non-fatal */
  }
}

async function testAiConnection() {
  aiProviderTestLoading.value = true;
  aiProviderTestResult.value = null;
  try {
    const acct = currentAccountId;
    const { data } = await axios.post(
      `/api/v1/accounts/${acct}/nexceo/settings/test_ai_connection`
    );
    aiProviderTestResult.value = data;
  } catch (e) {
    aiProviderTestResult.value = {
      success: false,
      message: e?.response?.data?.message || 'Test failed.',
    };
  } finally {
    aiProviderTestLoading.value = false;
    setTimeout(() => {
      aiProviderTestResult.value = null;
    }, 6000);
  }
}

onMounted(() => {
  load();
  loadWhatsappInboxes();
  loadAiProviderStatus();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-5 h-14 border-b border-n-weak bg-n-background shrink-0"
    >
      <div
        class="flex items-center justify-center w-7 h-7 rounded-full bg-woot-100 dark:bg-woot-500/20 shrink-0"
      >
        <span
          class="i-lucide-settings-2 size-3.5 text-woot-600 dark:text-woot-400"
        />
      </div>
      <div class="flex-1 min-w-0">
        <h1
          class="text-sm font-semibold text-n-slate-12 leading-tight truncate"
        >
          NEXCEO Settings
        </h1>
        <p class="text-xs text-n-slate-7 leading-tight hidden sm:block">
          AI, automation, notifications, integrations and company knowledge
        </p>
      </div>
      <span
        class="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border shrink-0"
        :class="
          effectiveAiEnabled
            ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30'
            : 'bg-n-alpha-2 text-n-slate-7 border-n-weak'
        "
      >
        <span
          class="size-1.5 rounded-full"
          :class="effectiveAiEnabled ? 'bg-success-500' : 'bg-n-slate-4'"
        />
        AI {{ effectiveAiEnabled ? 'Active' : 'Inactive' }}
      </span>
      <button
        :disabled="isSaving || isLoading"
        class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors shrink-0"
        @click="save"
      >
        <span
          class="size-3.5"
          :class="isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-save'"
        />
        Save
      </button>
    </div>

    <!-- Toast banners -->
    <div
      v-if="saveSuccess"
      class="flex items-center gap-2 px-5 py-2.5 bg-success-50 dark:bg-success-800/20 border-b border-success-200 dark:border-success-500/30 text-sm text-success-800 dark:text-success-400 shrink-0"
    >
      <span class="i-lucide-check-circle size-4 shrink-0" />
      Settings saved successfully.
      <button
        class="ml-auto i-lucide-x size-4 opacity-50 hover:opacity-100"
        @click="saveSuccess = false"
      />
    </div>
    <div
      v-if="saveError"
      class="flex items-center gap-2 px-5 py-2.5 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 dark:border-ruby-500/30 text-sm text-ruby-700 dark:text-ruby-400 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ saveError }}
      <button
        class="ml-auto i-lucide-x size-4 opacity-50 hover:opacity-100"
        @click="saveError = null"
      />
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
        <span class="text-sm">Loading…</span>
      </div>
    </div>

    <!-- Load error -->
    <div v-else-if="loadError" class="flex items-center justify-center flex-1">
      <div class="text-center">
        <span
          class="i-lucide-alert-circle size-8 text-ruby-500 mx-auto block mb-2"
        />
        <p class="text-sm text-n-slate-11 mb-3">{{ loadError }}</p>
        <button class="text-sm text-woot-500 hover:underline" @click="load">
          Retry
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div v-else class="flex-1 overflow-y-auto">
      <div class="max-w-4xl mx-auto px-5 py-5 space-y-4">
        <!-- Quick-access bar — visible to confirm bundle is loaded and for easy navigation -->
        <div
          class="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-xl bg-n-alpha-1 border border-n-weak text-xs text-n-slate-9"
        >
          <span class="i-lucide-layout-grid size-3.5" />
          <span class="font-medium text-n-slate-11">Quick access:</span>
          <a
            href="#nexceo-csv-exports"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-download size-3" /> CSV Exports
          </a>
          <router-link
            :to="`/app/accounts/${currentAccountId}/settings/integrations/nexceo_google`"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-google size-3" /> Google Integration ↗
          </router-link>
          <a
            href="#nexceo-gsheets-sync"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-table-2 size-3" /> Sheets Sync
          </a>
          <a
            href="#nexceo-company-knowledge"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-building-2 size-3" /> Company Knowledge
          </a>
          <a
            href="#nexceo-meeting-templates"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-video size-3" /> Meeting Templates
          </a>
          <router-link
            :to="`/app/accounts/${currentAccountId}/nexceo/runtime-inspector`"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-terminal-square size-3" /> Runtime Inspector
            ↗
          </router-link>
          <router-link
            :to="`/app/accounts/${currentAccountId}/nexceo/ai-workforce`"
            class="flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-n-alpha-2 hover:text-woot-600 transition-colors"
          >
            <span class="i-lucide-bot size-3" /> AI Workforce ↗
          </router-link>
          <span class="ml-auto text-n-slate-6 text-[10px]"
            >NEXCEO Settings v21.2</span
          >
        </div>

        <!-- ── A. AI Control Center ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-cpu size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              AI Control Center
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <!-- ENV warning -->
            <div
              v-if="!envAiEnabled"
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/20"
            >
              <span
                class="i-lucide-shield-x size-4 text-ruby-600 dark:text-ruby-400 shrink-0 mt-0.5"
              />
              <p class="text-xs text-ruby-800 dark:text-ruby-300">
                <strong
                  >AI is disabled by the server environment variable.</strong
                >
                UI settings cannot enable AI until
                <code class="font-mono">NEXCEO_AI_ENABLED=true</code> is set on
                the server.
              </p>
            </div>

            <!-- Status chips -->
            <div class="grid grid-cols-2 gap-3">
              <div
                class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
              >
                <span class="text-xs text-n-slate-8">Server ENV</span>
                <div class="flex items-center gap-1.5">
                  <span
                    class="size-2 rounded-full"
                    :class="envAiEnabled ? 'bg-success-500' : 'bg-ruby-500'"
                  />
                  <span
                    class="text-sm font-medium"
                    :class="
                      envAiEnabled
                        ? 'text-success-700 dark:text-success-400'
                        : 'text-ruby-700 dark:text-ruby-400'
                    "
                  >
                    {{ envAiEnabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div>
              <div
                class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
              >
                <span class="text-xs text-n-slate-8">Effective Status</span>
                <div class="flex items-center gap-1.5">
                  <span
                    class="size-2 rounded-full"
                    :class="
                      effectiveAiEnabled ? 'bg-success-500' : 'bg-ruby-500'
                    "
                  />
                  <span
                    class="text-sm font-medium"
                    :class="
                      effectiveAiEnabled
                        ? 'text-success-700 dark:text-success-400'
                        : 'text-ruby-700 dark:text-ruby-400'
                    "
                  >
                    {{ effectiveAiEnabled ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Toggles -->
            <div class="space-y-3">
              <div
                v-for="toggle in [
                  {
                    key: 'global_ai_enabled',
                    label: 'Global AI Enabled',
                    desc: 'Master switch for all AI auto-replies in this account.',
                    disabled: aiControlsDisabled,
                  },
                  {
                    key: 'auto_reply_enabled',
                    label: 'Auto Reply Enabled',
                    desc: 'Allow AI to send replies to incoming messages.',
                    disabled: aiControlsDisabled,
                  },
                  {
                    key: 'human_handoff_enabled',
                    label: 'Human Handoff Enabled',
                    desc: 'AI assigns a human agent when a handoff trigger is detected.',
                    disabled: false,
                  },
                  {
                    key: 'approval_queue_enabled',
                    label: 'Approval Queue Enabled',
                    desc: 'Sensitive documents require human approval before being shared.',
                    disabled: false,
                  },
                ]"
                :key="toggle.key"
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60 last:border-0"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ toggle.label }}
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">{{ toggle.desc }}</p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
                  :class="[
                    settings[toggle.key]
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4',
                    toggle.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                  ]"
                  :disabled="toggle.disabled"
                  @click="
                    !toggle.disabled &&
                      (settings[toggle.key] = !settings[toggle.key])
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings[toggle.key] ? 'translate-x-4' : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- ── B. Automation ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-zap size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Automation
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <div class="space-y-3">
              <div
                v-for="toggle in [
                  {
                    key: 'follow_up_enabled',
                    label: 'Follow-up Automation',
                    desc: 'Automatically send follow-up messages to overdue leads.',
                  },
                  {
                    key: 'site_visit_reminders_enabled',
                    label: 'Site Visit Reminders',
                    desc: 'Send reminders to leads and agents before scheduled site visits.',
                  },
                ]"
                :key="toggle.key"
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60 last:border-0"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ toggle.label }}
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">{{ toggle.desc }}</p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                  :class="
                    settings[toggle.key]
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="settings[toggle.key] = !settings[toggle.key]"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings[toggle.key] ? 'translate-x-4' : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div
                v-for="field in [
                  {
                    key: 'max_follow_ups_per_lead',
                    label: 'Max Follow-ups / Lead',
                    min: 1,
                    max: 20,
                  },
                  {
                    key: 'follow_up_cooldown_hours',
                    label: 'Follow-up Cooldown (hrs)',
                    min: 1,
                    max: 168,
                  },
                  {
                    key: 'pending_action_expiry_hours',
                    label: 'Approval Expiry (hrs)',
                    min: 1,
                    max: 720,
                  },
                ]"
                :key="field.key"
                class="flex flex-col gap-1"
              >
                <label class="text-xs font-medium text-n-slate-11">{{
                  field.label
                }}</label>
                <input
                  v-model.number="settings[field.key]"
                  type="number"
                  :min="field.min"
                  :max="field.max"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- ── C. Working Hours ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-clock size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Working Hours
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <div class="flex items-center justify-between gap-3 py-2">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Working Hours Enforcement
                </p>
                <p class="text-xs text-n-slate-8 mt-0.5">
                  AI will not reply outside configured hours.
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                :class="
                  settings.working_hours_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                "
                @click="
                  settings.working_hours_enabled =
                    !settings.working_hours_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                  :class="
                    settings.working_hours_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11"
                >Timezone</label
              >
              <select
                v-model="settings.timezone"
                class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
              >
                <option v-for="tz in TIMEZONES" :key="tz" :value="tz">
                  {{ tz }}
                </option>
              </select>
            </div>

            <div v-if="settings.working_hours_enabled">
              <p class="text-xs font-medium text-n-slate-11 mb-2">
                Day Schedule
              </p>
              <div class="space-y-1">
                <div
                  v-for="day in DAYS"
                  :key="day"
                  class="flex items-center gap-2 py-1.5 border-b border-n-weak/50 last:border-0"
                >
                  <span
                    class="w-8 text-xs font-semibold text-n-slate-9 shrink-0"
                    >{{ dayLabel(day) }}</span
                  >
                  <label
                    class="flex items-center gap-1 cursor-pointer shrink-0"
                  >
                    <input
                      type="checkbox"
                      :checked="getDay(day).closed"
                      class="rounded border-n-weak"
                      @change="
                        setDayField(day, 'closed', $event.target.checked)
                      "
                    />
                    <span class="text-xs text-n-slate-9">Closed</span>
                  </label>
                  <template v-if="!getDay(day).closed">
                    <input
                      type="time"
                      :value="getDay(day).open"
                      class="text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-11 focus:outline-none focus:border-woot-500"
                      @change="setDayField(day, 'open', $event.target.value)"
                    />
                    <span class="text-xs text-n-slate-7">to</span>
                    <input
                      type="time"
                      :value="getDay(day).close"
                      class="text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-11 focus:outline-none focus:border-woot-500"
                      @change="setDayField(day, 'close', $event.target.value)"
                    />
                  </template>
                  <!-- eslint-disable-next-line vue/max-attributes-per-line -->
                  <span v-else class="text-xs text-n-slate-7 italic"
                    >Closed all day</span
                  >
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ── D. AI Provider Defaults ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-sparkles size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              AI Provider Defaults
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <div
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30"
            >
              <span
                class="i-lucide-info size-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
              />
              <p class="text-xs text-amber-800 dark:text-amber-300">
                API keys can be set via environment variables (<code
                  class="font-mono"
                  >NEXCEO_OPENAI_API_KEY</code
                >, <code class="font-mono">NEXCEO_ANTHROPIC_API_KEY</code>) or
                configured in
                <router-link
                  :to="`/app/accounts/${currentAccountId}/settings/integrations/openai`"
                  class="underline text-amber-700 dark:text-amber-300 hover:text-woot-500"
                >
                  Settings → Integrations → OpenAI </router-link
                >. NEXCEO AI automatically reads the API key from that page.
                Per-project overrides are in AI Employee Settings.
              </p>
            </div>

            <!-- AI Provider live status -->
            <div
              v-if="aiProviderStatus"
              class="flex items-center justify-between gap-3 p-3 rounded-lg border"
              :class="
                aiProviderStatus.configured
                  ? 'bg-success-50 border-success-200'
                  : 'bg-ruby-50 border-ruby-200'
              "
            >
              <div class="flex items-center gap-2">
                <span
                  class="size-4 shrink-0"
                  :class="
                    aiProviderStatus.configured
                      ? 'i-lucide-check-circle text-success-600'
                      : 'i-lucide-alert-circle text-ruby-600'
                  "
                />
                <div>
                  <p
                    class="text-sm font-medium"
                    :class="
                      aiProviderStatus.configured
                        ? 'text-success-800'
                        : 'text-ruby-700'
                    "
                  >
                    {{
                      aiProviderStatus.configured
                        ? 'AI Provider Configured'
                        : 'AI Provider Not Configured'
                    }}
                  </p>
                  <p
                    class="text-xs opacity-80"
                    :class="
                      aiProviderStatus.configured
                        ? 'text-success-700'
                        : 'text-ruby-600'
                    "
                  >
                    {{ aiProviderStatus.provider }} ·
                    {{ aiProviderStatus.model }}
                    <span v-if="aiProviderStatus.source === 'chatwoot_hook'">
                      · from Integrations → OpenAI</span
                    >
                    <span v-else-if="aiProviderStatus.source === 'env'">
                      · from server ENV</span
                    >
                    <span v-else-if="aiProviderStatus.source === 'nexceo_db'">
                      · from NEXCEO settings</span
                    >
                    <span v-else-if="!aiProviderStatus.configured">
                      · configure in Integrations → OpenAI</span
                    >
                  </p>
                </div>
              </div>
              <button
                :disabled="aiProviderTestLoading"
                class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors whitespace-nowrap"
                @click="testAiConnection"
              >
                <span
                  v-if="aiProviderTestLoading"
                  class="i-lucide-loader-2 size-3.5 animate-spin inline mr-1"
                />
                Test Connection
              </button>
            </div>
            <div
              v-if="aiProviderTestResult"
              class="text-xs px-3 py-2 rounded-lg border"
              :class="
                aiProviderTestResult.success
                  ? 'bg-success-50 text-success-700 border-success-200'
                  : 'bg-ruby-50 text-ruby-700 border-ruby-200'
              "
            >
              {{ aiProviderTestResult.message }}
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >Default Provider</label
                >
                <select
                  v-model="settings.default_ai_provider"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                >
                  <option v-for="p in PROVIDERS" :key="p" :value="p">
                    {{ p }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >Default Model</label
                >
                <select
                  v-model="settings.default_ai_model"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                >
                  <option value="">Use provider default</option>
                  <option v-for="m in primaryModels" :key="m" :value="m">
                    {{ m }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >Fallback Provider</label
                >
                <select
                  v-model="settings.fallback_ai_provider"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                >
                  <option value="">None</option>
                  <option v-for="p in PROVIDERS" :key="p" :value="p">
                    {{ p }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >Fallback Model</label
                >
                <select
                  v-model="settings.fallback_ai_model"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                >
                  <option value="">Use provider default</option>
                  <option v-for="m in fallbackModels" :key="m" :value="m">
                    {{ m }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </section>

        <!-- ── E. Limits ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-gauge size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Limits
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >Max AI Replies / Conversation</label
                >
                <input
                  v-model.number="settings.max_ai_replies_per_conversation"
                  type="number"
                  min="1"
                  max="200"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                />
              </div>
              <div class="flex flex-col gap-1 opacity-50">
                <label class="text-xs font-medium text-n-slate-11"
                  >Daily Cost Limit ($)</label
                >
                <input
                  disabled
                  :value="settings.daily_ai_cost_limit"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Not enforced"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-9 placeholder:text-n-slate-7 cursor-not-allowed"
                />
              </div>
              <div class="flex flex-col gap-1 opacity-50">
                <label class="text-xs font-medium text-n-slate-11"
                  >Monthly Cost Limit ($)</label
                >
                <input
                  disabled
                  :value="settings.monthly_ai_cost_limit"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Not enforced"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-9 placeholder:text-n-slate-7 cursor-not-allowed"
                />
              </div>
            </div>
            <div
              class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
            >
              <span class="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
                >⚠</span
              >
              <p class="text-xs text-amber-700 dark:text-amber-300">
                <strong>Cost cap enforcement is not yet active.</strong> These
                fields are reserved for a future billing module. Setting a value
                here does not limit AI spending. Monitor usage in AI Analytics.
              </p>
            </div>
          </div>
        </section>

        <!-- ── F. Notifications ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span class="i-lucide-bell size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Notifications
            </h2>
          </div>
          <div class="px-5 py-4 space-y-5">
            <p
              class="text-xs text-n-slate-8 leading-relaxed bg-n-alpha-1 border border-n-weak rounded-lg px-3 py-2"
            >
              Project team members are the primary recipients. Private notes are
              always created as the guaranteed fallback inside Chatwoot.
            </p>
            <!-- Master + channel toggles -->
            <div class="space-y-3">
              <!-- Master switch -->
              <div
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Agent Notifications (Master Switch)
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Disabling this suppresses all channels.
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                  :class="
                    settings.agent_notifications_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="
                    settings.agent_notifications_enabled =
                      !settings.agent_notifications_enabled
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings.agent_notifications_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>

              <!-- Private Note channel (always on) -->
              <div
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Private Note (Chatwoot)
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Guaranteed internal fallback inside Chatwoot — always
                    created regardless of other channel settings.
                  </p>
                </div>
                <span
                  class="text-xs font-medium text-green-600 dark:text-green-400 px-2 py-0.5 bg-green-50 dark:bg-green-900/20 rounded-full"
                  >Always On</span
                >
              </div>

              <!-- Email channel -->
              <div
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Email Notifications
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Sends email alerts to each project team member's contact
                    email.
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                  :class="
                    settings.notification_email_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="
                    settings.notification_email_enabled =
                      !settings.notification_email_enabled
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings.notification_email_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>

              <!-- Chatwoot WhatsApp channel -->
              <div
                class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Chatwoot WhatsApp Notifications
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Uses a Chatwoot WhatsApp inbox to send alerts to project
                    team members. Each member gets their own staff conversation
                    — alerts are never posted in the customer thread.
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                  :class="
                    settings.notification_chatwoot_whatsapp_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="
                    settings.notification_chatwoot_whatsapp_enabled =
                      !settings.notification_chatwoot_whatsapp_enabled
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings.notification_chatwoot_whatsapp_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>

              <!-- Webhook channel -->
              <div class="flex items-center justify-between gap-3 py-2">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Webhook Notifications
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Optional external provider integration. POST JSON with
                    recipient routing to WATI, Interakt, AiSensy, Twilio, or a
                    custom service.
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                  :class="
                    settings.notification_webhook_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="
                    settings.notification_webhook_enabled =
                      !settings.notification_webhook_enabled
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      settings.notification_webhook_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </div>

            <!-- Chatwoot WhatsApp config -->
            <div
              v-if="settings.notification_chatwoot_whatsapp_enabled"
              class="border border-n-weak rounded-lg p-4 space-y-3 bg-n-alpha-1"
            >
              <p
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                WhatsApp Inbox Configuration
              </p>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >WhatsApp Inbox</label
                >
                <select
                  v-model="settings.notification_chatwoot_whatsapp_inbox_id"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                >
                  <option :value="null">-- Select WhatsApp inbox --</option>
                  <option
                    v-for="inbox in whatsappInboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }} ({{ whatsappProviderLabel(inbox) }})
                  </option>
                </select>
                <p
                  v-if="whatsappInboxes.length === 0"
                  class="text-xs text-amber-600 dark:text-amber-400 mt-0.5"
                >
                  No WhatsApp inboxes found. Add a WhatsApp inbox in Chatwoot
                  Settings first.
                </p>
                <p v-else class="text-xs text-n-slate-7 mt-0.5">
                  Alerts will be sent through this inbox to each team member's
                  WhatsApp number.
                </p>
              </div>
              <div class="flex flex-col gap-1.5">
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="
                      settings.notification_chatwoot_whatsapp_template_mode
                    "
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                  />
                  Template mode (sends via approved WhatsApp template)
                </label>
                <p class="text-xs text-n-slate-7 pl-6">
                  Enable when the 24-hour WhatsApp session window is closed for
                  your staff conversations. Requires an approved template in
                  your WhatsApp Business account. Default template name:
                  <code class="bg-n-alpha-2 px-1 rounded font-mono"
                    >nexceo_team_alert</code
                  >
                  (6 body variables: event, project, lead name, phone, summary,
                  URL). The template must be synced in Chatwoot (Settings →
                  Inboxes → Sync Templates).
                </p>
              </div>
              <div
                v-if="settings.notification_chatwoot_whatsapp_template_mode"
                class="flex flex-col gap-1"
              >
                <label class="text-xs font-medium text-n-slate-11"
                  >Template Name</label
                >
                <input
                  v-model="
                    settings.notification_chatwoot_whatsapp_default_template
                  "
                  type="text"
                  placeholder="nexceo_team_alert"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                />
                <p class="text-xs text-n-slate-7">
                  Must match the approved template name exactly
                  (case-sensitive). Language code used:
                  <code class="bg-n-alpha-2 px-1 rounded font-mono">en</code>.
                  Leave blank to use the default
                  <code class="bg-n-alpha-2 px-1 rounded font-mono"
                    >nexceo_team_alert</code
                  >.
                </p>
              </div>
            </div>

            <!-- Webhook URL -->
            <div
              v-if="settings.notification_webhook_enabled"
              class="flex flex-col gap-1"
            >
              <label class="text-xs font-medium text-n-slate-11">
                Webhook URL
              </label>
              <input
                v-model="settings.notification_webhook_url"
                type="url"
                placeholder="https://hooks.example.com/nexceo"
                class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
              />
              <p class="text-xs text-n-slate-7 mt-0.5">
                Webhook secret is configured via
                <code class="bg-n-alpha-2 px-1 rounded"
                  >NEXCEO_NOTIFICATION_WEBHOOK_SECRET</code
                >
                environment variable and is never stored in the database. The
                webhook payload includes each recipient's role, email, and
                WhatsApp number for external routing.
              </p>
            </div>

            <!-- Enabled events -->
            <div>
              <p
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-2"
              >
                Enabled Events
              </p>
              <div class="space-y-2">
                <div
                  v-for="ev in NOTIFICATION_EVENTS"
                  :key="ev.key"
                  class="flex items-center justify-between py-1.5"
                >
                  <span class="text-sm text-n-slate-12">{{ ev.label }}</span>
                  <button
                    class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                    :class="
                      settings.notification_events_enabled[ev.key]
                        ? 'bg-woot-500'
                        : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                    "
                    @click="
                      settings.notification_events_enabled[ev.key] =
                        !settings.notification_events_enabled[ev.key]
                    "
                  >
                    <span
                      class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                      :class="
                        settings.notification_events_enabled[ev.key]
                          ? 'translate-x-4'
                          : 'translate-x-0.5'
                      "
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Retry settings -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1"
                  >Max Retry Attempts</label
                >
                <input
                  v-model.number="settings.notification_max_attempts"
                  type="number"
                  min="1"
                  max="10"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 w-full focus:outline-none focus:border-woot-500"
                />
              </div>
              <div class="flex items-end pb-1">
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="settings.notification_retry_enabled"
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                  />
                  Auto-retry failed deliveries
                </label>
              </div>
            </div>

            <!-- Test buttons -->
            <div>
              <p
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-2"
              >
                Test Delivery
              </p>
              <div class="flex flex-wrap gap-2">
                <!-- private_note / email / webhook — simple one-click -->
                <button
                  v-for="ch in ['private_note', 'email', 'webhook']"
                  :key="ch"
                  class="px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  :disabled="testNotifLoading"
                  @click="testNotification(ch)"
                >
                  <span
                    v-if="testNotifLoading"
                    class="i-lucide-loader-2 size-3.5 animate-spin"
                  />
                  <span v-else class="i-lucide-send size-3.5" />
                  Test {{ ch.replace(/_/g, ' ') }}
                </button>
                <!-- WhatsApp — opens inline recipient form -->
                <button
                  class="px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  :disabled="testNotifLoading"
                  @click="openWhatsappTestForm"
                >
                  <span class="i-lucide-message-circle size-3.5" />
                  Test chatwoot whatsapp
                </button>
              </div>

              <!-- WhatsApp test inline form -->
              <div
                v-if="waTestFormOpen"
                class="mt-3 border border-n-weak rounded-lg p-4 space-y-3 bg-n-alpha-1"
              >
                <p
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  WhatsApp Test — Choose Recipient
                </p>

                <!-- Team member picker -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">
                    Project Team Member
                    <span class="text-n-slate-7 font-normal"
                      >(must have WhatsApp number)</span
                    >
                  </label>
                  <select
                    v-model="waTestMemberId"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                    @change="waTestPhone = ''"
                  >
                    <option value="">-- select a team member --</option>
                    <option
                      v-for="r in waTestRecipients"
                      :key="r.id"
                      :value="r.id"
                    >
                      {{ r.display_name }} · {{ r.whatsapp_number }} ·
                      {{ r.project_name }}
                    </option>
                  </select>
                  <p
                    v-if="
                      !waTestRecipientsLoading && waTestRecipients.length === 0
                    "
                    class="text-xs text-amber-600 dark:text-amber-400"
                  >
                    No team members with WhatsApp numbers found. Add a number in
                    Projects → Team Members.
                  </p>
                </div>

                <p class="text-xs text-n-slate-7 text-center">— or —</p>

                <!-- Manual phone input -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11"
                    >Manual Test Phone</label
                  >
                  <input
                    v-model="waTestPhone"
                    type="text"
                    placeholder="+919876543210"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                    @input="waTestMemberId = ''"
                  />
                  <p class="text-xs text-n-slate-7">
                    E.164 format recommended. A staff contact and conversation
                    will be created in your WhatsApp inbox.
                  </p>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-2 pt-1">
                  <button
                    class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                    :disabled="
                      testNotifLoading ||
                      (!waTestMemberId && !waTestPhone.trim())
                    "
                    @click="sendWhatsappTest"
                  >
                    <span
                      v-if="testNotifLoading"
                      class="i-lucide-loader-2 size-3.5 animate-spin"
                    />
                    <span v-else class="i-lucide-send size-3.5" />
                    Send Test
                  </button>
                  <button
                    class="px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                    @click="
                      waTestFormOpen = false;
                      testNotifResult = null;
                    "
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <!-- Result banner -->
              <div
                v-if="testNotifResult"
                class="mt-2 text-xs px-3 py-2 rounded-lg space-y-1"
                :class="
                  testNotifResult.success
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                "
              >
                <p class="font-medium">{{ testNotifResult.message }}</p>

                <!-- Template mode details -->
                <template v-if="testNotifResult.templateModeUsed">
                  <p>
                    Template:
                    <code class="font-mono">{{
                      testNotifResult.templateName
                    }}</code>
                    · Language:
                    <code class="font-mono">{{
                      testNotifResult.templateLanguage
                    }}</code>
                    · Category:
                    <code class="font-mono">{{
                      testNotifResult.templateCategory
                    }}</code>
                    · Variables: {{ testNotifResult.templateVariablesCount }}
                  </p>
                  <p>
                    Template params attached:
                    <strong>{{
                      testNotifResult.hasTemplateParams === true
                        ? 'YES ✓'
                        : 'NO ✗'
                    }}</strong>
                    <span
                      v-if="testNotifResult.hasTemplateParams !== true"
                      class="ml-1 opacity-80"
                    >
                      — check Chatwoot → Messages for external_error
                    </span>
                  </p>
                </template>
                <template v-else-if="testNotifResult.status === 'delivered'">
                  <p class="opacity-75">Mode: free-form (template mode off)</p>
                </template>

                <!-- IDs -->
                <p
                  v-if="
                    testNotifResult.conversationId || testNotifResult.messageId
                  "
                  class="opacity-70"
                >
                  <span v-if="testNotifResult.messageId"
                    >Message #{{ testNotifResult.messageId }}</span
                  >
                  <span v-if="testNotifResult.conversationId">
                    · Conv #{{ testNotifResult.conversationId }}</span
                  >
                  <span v-if="testNotifResult.deliveryId">
                    · Delivery #{{ testNotifResult.deliveryId }}</span
                  >
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- ── G. Admin Daily Report ── -->
        <section
          class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span
              class="i-lucide-file-bar-chart size-3.5 text-n-slate-8 shrink-0"
            />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Admin Daily Report
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <p
              class="text-xs text-n-slate-8 leading-relaxed bg-n-alpha-1 border border-n-weak rounded-lg px-3 py-2"
            >
              Admin summary sent once per day. Recipients are separate from
              per-project instant alerts. WhatsApp daily reports may require an
              approved template if the 24-hour window is closed.
            </p>
            <!-- Master toggle -->
            <div
              class="flex items-center justify-between gap-3 py-2 border-b border-n-weak/60"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Enable Daily Report
                </p>
                <p class="text-xs text-n-slate-8 mt-0.5">
                  Send a daily summary to admin recipients at the configured
                  time.
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer"
                :class="
                  settings.daily_admin_report_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                "
                @click="
                  settings.daily_admin_report_enabled =
                    !settings.daily_admin_report_enabled
                "
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                  :class="
                    settings.daily_admin_report_enabled
                      ? 'translate-x-4'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>

            <div v-if="settings.daily_admin_report_enabled" class="space-y-4">
              <!-- Time + Timezone -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11"
                    >Report Time (24h)</label
                  >
                  <input
                    v-model="settings.daily_admin_report_time"
                    type="time"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11"
                    >Timezone</label
                  >
                  <select
                    v-model="settings.daily_admin_report_timezone"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                  >
                    <option v-for="tz in TIMEZONES" :key="tz" :value="tz">
                      {{ tz }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Channel toggles -->
              <div class="space-y-2">
                <p
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Delivery Channels
                </p>
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="settings.daily_admin_report_whatsapp_enabled"
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                  />
                  WhatsApp
                </label>
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="settings.daily_admin_report_email_enabled"
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                  />
                  Email
                </label>
              </div>

              <!-- Recipients -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11"
                    >WhatsApp Numbers</label
                  >
                  <textarea
                    v-model="whatsappNumbersInput"
                    rows="2"
                    placeholder="+919876543210, +919000000001"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 resize-none"
                    @blur="syncWhatsappNumbers"
                  />
                  <p class="text-xs text-n-slate-7">
                    Comma-separated E.164 numbers. Uses the configured WhatsApp
                    inbox.
                  </p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11"
                    >Email Addresses</label
                  >
                  <textarea
                    v-model="reportEmailsInput"
                    rows="2"
                    placeholder="admin@company.com, manager@company.com"
                    class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 resize-none"
                    @blur="syncReportEmails"
                  />
                  <p class="text-xs text-n-slate-7">
                    Comma-separated email addresses.
                  </p>
                </div>
              </div>

              <!-- Template name -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11"
                  >WhatsApp Template Name</label
                >
                <input
                  v-model="settings.daily_admin_report_template_name"
                  type="text"
                  placeholder="nexceo_daily_report"
                  class="text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                />
                <p class="text-xs text-n-slate-7">
                  Use the approved WhatsApp template
                  <code class="font-mono bg-n-alpha-1 px-1 rounded"
                    >nexceo_daily_report</code
                  >
                  for daily admin report delivery. Sync it in Chatwoot inbox
                  settings after Meta approval. Template must have 6 body
                  variables: date, lead summary, site visits, top projects, AI
                  summary, dashboard URL.
                </p>
                <p
                  class="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1"
                >
                  <span class="i-lucide-info size-3.5 mt-0.5 shrink-0" />
                  After Meta template approval, go to Chatwoot → Settings →
                  Inboxes → WhatsApp inbox → Sync Templates, then test daily
                  report delivery.
                </p>
              </div>

              <!-- Sections to include -->
              <div class="space-y-2">
                <p
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Include Sections
                </p>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <label
                    v-for="toggle in [
                      {
                        key: 'daily_admin_report_include_projects',
                        label: 'Projects',
                      },
                      {
                        key: 'daily_admin_report_include_agents',
                        label: 'Agents',
                      },
                      {
                        key: 'daily_admin_report_include_ai_summary',
                        label: 'AI Summary',
                      },
                      {
                        key: 'daily_admin_report_include_notifications',
                        label: 'Notifications',
                      },
                    ]"
                    :key="toggle.key"
                    class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                  >
                    <input
                      v-model="settings[toggle.key]"
                      type="checkbox"
                      class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                    />
                    {{ toggle.label }}
                  </label>
                </div>
              </div>

              <!-- Quick link to Reports dashboard -->
              <div class="pt-1">
                <a
                  :href="`/app/accounts/${currentAccountId}/nexceo/reports`"
                  class="text-sm text-woot-500 hover:underline flex items-center gap-1"
                >
                  <span class="i-lucide-bar-chart-2 size-3.5" />
                  Open Reports Dashboard
                </a>
              </div>
            </div>
          </div>
        </section>

        <!-- ── H. CSV Exports ─────────────────────────────────────────── -->
        <section
          id="nexceo-csv-exports"
          class="bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center gap-2 px-5 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="i-lucide-download size-3.5 text-n-slate-8 shrink-0" />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              CSV Exports
            </h2>
          </div>
          <div class="px-5 py-4 space-y-3">
            <p class="text-xs text-n-slate-8">
              Download NEXCEO data as CSV for backup, reporting, or external
              analysis. Exports respect active filters.
            </p>
            <div
              v-if="exportError"
              class="text-xs text-ruby-700 bg-ruby-50 border border-ruby-200 rounded-lg px-3 py-2"
            >
              {{ exportError }}
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="exp in [
                  {
                    type: 'verified_leads',
                    label: 'Verified Leads',
                    icon: 'i-lucide-user-check',
                  },
                  { type: 'leads', label: 'All Leads', icon: 'i-lucide-users' },
                  {
                    type: 'site_visits',
                    label: 'Site Visits',
                    icon: 'i-lucide-calendar',
                  },
                  {
                    type: 'kanban',
                    label: 'Kanban Pipeline',
                    icon: 'i-lucide-columns',
                  },
                  {
                    type: 'reports',
                    label: 'Reports',
                    icon: 'i-lucide-bar-chart-2',
                    adminOnly: true,
                  },
                  {
                    type: 'notifications',
                    label: 'Notifications',
                    icon: 'i-lucide-bell',
                    adminOnly: true,
                  },
                  {
                    type: 'approvals',
                    label: 'AI Approvals',
                    icon: 'i-lucide-shield-check',
                    adminOnly: true,
                  },
                ]"
                :key="exp.type"
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 text-xs font-medium hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
                :disabled="exportLoading === exp.type"
                @click="downloadExport(exp.type)"
              >
                <span
                  class="size-3.5"
                  :class="[
                    exportLoading === exp.type
                      ? 'i-lucide-loader-2 animate-spin'
                      : exp.icon,
                  ]"
                />
                {{ exp.label }}
              </button>
            </div>
          </div>
        </section>

        <!-- ── I. Google Integration (moved to Settings → Integrations) ─── -->
        <section
          id="nexceo-gcal"
          class="bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-5 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-link size-3.5 text-n-slate-8 shrink-0" />
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Google Integration
              </h2>
              <span class="text-xs text-n-slate-6"
                >Calendar · Meet · Sheets</span
              >
            </div>
            <router-link
              :to="`/app/accounts/${currentAccountId}/settings/integrations/nexceo_google`"
              class="flex items-center gap-1.5 text-xs text-woot-500 hover:underline font-medium"
            >
              <span class="i-lucide-external-link size-3.5" />
              Manage in Integrations
            </router-link>
          </div>
          <div class="px-5 py-4 flex flex-col gap-3">
            <!-- Connection status row -->
            <div class="flex items-center gap-3">
              <div
                class="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 border text-sm"
                :class="
                  settings.google_oauth_connected
                    ? 'bg-success-50 border-success-200 text-success-700'
                    : settings.google_oauth_credentials_source !== 'missing'
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-n-alpha-1 border-n-weak text-n-slate-9'
                "
              >
                <span
                  class="size-4 shrink-0"
                  :class="
                    settings.google_oauth_connected
                      ? 'i-lucide-check-circle text-success-600'
                      : settings.google_oauth_credentials_source !== 'missing'
                        ? 'i-lucide-alert-circle text-amber-600'
                        : 'i-lucide-x-circle text-n-slate-7'
                  "
                />
                <span>
                  <span v-if="settings.google_oauth_connected">
                    Connected as
                    {{
                      settings.google_oauth_connected_email || 'Google account'
                    }}
                  </span>
                  <span
                    v-else-if="
                      settings.google_oauth_credentials_source !== 'missing'
                    "
                  >
                    Credentials configured — not yet connected
                  </span>
                  <span v-else>Not configured</span>
                </span>
              </div>
            </div>
            <!-- Feature status chips -->
            <div class="flex flex-wrap gap-2 text-xs">
              <span
                class="flex items-center gap-1 px-2 py-1 rounded-full border font-medium"
                :class="
                  settings.google_calendar_enabled &&
                  settings.google_oauth_connected
                    ? 'bg-success-50 text-success-700 border-success-200'
                    : 'bg-n-alpha-1 text-n-slate-8 border-n-weak'
                "
              >
                <span class="i-lucide-calendar size-3" />
                Calendar {{ settings.google_calendar_enabled ? 'On' : 'Off' }}
              </span>
              <span
                class="flex items-center gap-1 px-2 py-1 rounded-full border font-medium"
                :class="
                  settings.google_calendar_enabled &&
                  settings.google_calendar_add_meet_for_video_calls &&
                  settings.google_oauth_connected
                    ? 'bg-success-50 text-success-700 border-success-200'
                    : 'bg-n-alpha-1 text-n-slate-8 border-n-weak'
                "
              >
                <span class="i-lucide-video size-3" />
                Meet
                {{
                  settings.google_calendar_add_meet_for_video_calls
                    ? 'On'
                    : 'Off'
                }}
              </span>
              <span
                class="flex items-center gap-1 px-2 py-1 rounded-full border font-medium"
                :class="
                  settings.google_sheets_sync_enabled
                    ? 'bg-success-50 text-success-700 border-success-200'
                    : 'bg-n-alpha-1 text-n-slate-8 border-n-weak'
                "
              >
                <span class="i-lucide-table-2 size-3" />
                Sheets Sync
                {{ settings.google_sheets_sync_enabled ? 'On' : 'Off' }}
              </span>
            </div>
            <p class="text-xs text-n-slate-7 flex items-center gap-1">
              <span class="i-lucide-info size-3" />
              Google configuration (OAuth, Calendar ID, Sheets ID) has moved to
              <router-link
                :to="`/app/accounts/${currentAccountId}/settings/integrations/nexceo_google`"
                class="text-woot-500 hover:underline"
              >
                Settings → Integrations → Google </router-link
              >.
            </p>
          </div>
        </section>

        <!-- ── Company Knowledge ─────────────────────────────────────── -->
        <section
          id="nexceo-company-knowledge"
          class="bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
          >
            <span
              class="i-lucide-building-2 size-3.5 text-n-slate-8 shrink-0"
            />
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Company Knowledge
            </h2>
          </div>
          <div class="px-5 py-4 space-y-4">
            <p
              class="text-xs text-n-slate-8 leading-relaxed bg-woot-50 dark:bg-woot-800/10 border border-woot-100 dark:border-woot-700/30 rounded-lg px-3 py-2"
            >
              These details are included in the AI prompt so the AI can answer
              company-level questions naturally — "Who are you?", "Where is your
              office?", "Why should I trust you?"
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Company Name</label
                >
                <input
                  v-model="settings.company_name"
                  type="text"
                  placeholder="e.g. NEXCEO Realty"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Tagline</label
                >
                <input
                  v-model="settings.company_tagline"
                  type="text"
                  placeholder="e.g. Trusted real-estate advisors"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >About Company</label
                >
                <textarea
                  v-model="settings.company_about"
                  rows="3"
                  placeholder="Brief description — what your company does, how long, RERA details, etc."
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 resize-none transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Office Address</label
                >
                <input
                  v-model="settings.office_address"
                  type="text"
                  placeholder="e.g. 3rd Floor, ABC Tower, Bhubaneswar"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Contact Phone</label
                >
                <input
                  v-model="settings.contact_phone"
                  type="text"
                  placeholder="e.g. +91 98765 43210"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Contact Email</label
                >
                <input
                  v-model="settings.contact_email"
                  type="email"
                  placeholder="e.g. info@nexceo.in"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Website URL</label
                >
                <input
                  v-model="settings.website_url"
                  type="url"
                  placeholder="e.g. https://nexceo.in"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Brand Tone</label
                >
                <input
                  v-model="settings.brand_tone"
                  type="text"
                  placeholder="e.g. professional and warm"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
                />
                <p class="mt-1 text-xs text-n-slate-7">
                  Guides the overall style of AI replies.
                </p>
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Why Choose Us</label
                >
                <textarea
                  v-model="settings.why_choose_us"
                  rows="2"
                  placeholder="e.g. RERA registered, transparent process, 500+ satisfied clients since 2015."
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 resize-none transition-colors"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Company FAQs
                  <span class="font-normal text-n-slate-7"
                    >(one per line)</span
                  ></label
                >
                <textarea
                  v-model="faqsInput"
                  rows="4"
                  placeholder="e.g.&#10;Are you RERA registered? Yes, RERA no. ODISHA/12345.&#10;Do you charge brokerage? No, our service is free for buyers."
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 resize-none font-mono transition-colors"
                  @blur="syncFaqs"
                />
                <p class="mt-1 text-xs text-n-slate-7">
                  AI will use these to answer common company questions
                  naturally.
                </p>
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Forbidden Claims
                  <span class="font-normal text-n-slate-7"
                    >(one per line)</span
                  ></label
                >
                <textarea
                  v-model="forbiddenClaimsInput"
                  rows="3"
                  placeholder="e.g.&#10;guaranteed returns&#10;100% assured possession by Dec 2025&#10;no EMI for 2 years"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500 resize-none font-mono transition-colors"
                  @blur="syncForbiddenClaims"
                />
                <p class="mt-1 text-xs text-n-slate-7">
                  AI will never say these things, even if asked.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- ── WhatsApp Follow-up Templates ──────────────────────────────── -->
        <section
          class="bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-message-circle size-3.5 text-n-slate-8 shrink-0"
              />
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                WhatsApp Follow-up Templates
              </h2>
            </div>
            <button
              :disabled="templateSyncLoading"
              class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
              @click="checkFollowupTemplates"
            >
              <span
                v-if="templateSyncLoading"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-refresh-cw size-3.5" />
              Check Template Sync
            </button>
          </div>
          <div class="px-5 py-4 space-y-4">
            <!-- Prominent warning when AI is enabled but follow-up templates are missing -->
            <div
              v-if="effectiveAiEnabled"
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600/30"
            >
              <span
                class="i-lucide-alert-triangle size-3.5 text-amber-600 shrink-0 mt-0.5"
              />
              <p class="text-xs text-amber-800 dark:text-amber-300">
                <strong
                  >AI follow-up automation requires these 4 templates to be
                  synced.</strong
                >
                Without them, AI follow-up messages to WhatsApp leads whose
                24-hour session is closed will be skipped. Create them in Meta
                Business Manager, then sync in Chatwoot → Settings → Inboxes →
                WhatsApp → Sync Templates.
              </p>
            </div>
            <p class="text-xs text-n-slate-8 leading-relaxed">
              When a WhatsApp lead's 24-hour session window has expired, the AI
              uses pre-approved Meta templates for follow-ups instead of
              free-form messages. Create these templates in Meta Business
              Manager and sync them in
              <strong
                >Chatwoot → Settings → Inboxes → WhatsApp → Sync
                Templates</strong
              >.
            </p>

            <!-- Template list -->
            <div class="space-y-2">
              <div
                v-for="(templateName, stage) in {
                  'new_enquiry / lead_validation / qualified':
                    'nexceo_followup_general',
                  document_shared: 'nexceo_brochure_followup',
                  site_visit_scheduled: 'nexceo_site_visit_reminder',
                  site_visit_done: 'nexceo_post_visit_followup',
                }"
                :key="templateName"
                class="flex items-center justify-between px-3 py-2.5 rounded-lg border border-n-weak bg-n-alpha-1"
              >
                <div>
                  <p class="text-xs font-mono font-semibold text-n-slate-12">
                    {{ templateName }}
                  </p>
                  <p class="text-xs text-n-slate-7 mt-0.5">
                    Stage: {{ stage }}
                  </p>
                </div>
                <div
                  v-if="templateSyncResult && !templateSyncResult.error"
                  class="shrink-0 ml-3"
                >
                  <span
                    v-if="templateSyncResult.templates?.[templateName]?.found"
                    class="flex items-center gap-1 text-xs text-success-700 bg-success-50 border border-success-200 px-2 py-0.5 rounded-full font-medium"
                  >
                    <span class="i-lucide-check-circle size-3.5" />
                    Synced
                    <span
                      v-if="templateSyncResult.templates[templateName].language"
                      class="text-success-600 font-normal"
                      >({{
                        templateSyncResult.templates[templateName].language
                      }})</span
                    >
                  </span>
                  <span
                    v-else
                    class="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium"
                  >
                    <span class="i-lucide-alert-circle size-3.5" />
                    Not synced
                  </span>
                </div>
                <!-- eslint-disable-next-line vue/max-attributes-per-line -->
                <span v-else class="text-xs text-n-slate-6 shrink-0 ml-3"
                  >–</span
                >
              </div>
            </div>

            <!-- Sync result banner -->
            <div v-if="templateSyncResult">
              <div
                v-if="templateSyncResult.error"
                class="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              >
                <span class="i-lucide-alert-circle size-3.5 shrink-0 mt-0.5" />
                {{ templateSyncResult.error }}
              </div>
              <div
                v-else-if="templateSyncResult.all_synced"
                class="flex items-center gap-2 text-xs text-success-700 bg-success-50 border border-success-200 rounded-lg px-3 py-2 font-medium"
              >
                <span class="i-lucide-check-circle size-3.5" />
                All 4 templates synced on inbox "{{
                  templateSyncResult.inbox_name
                }}".
              </div>
              <div
                v-else
                class="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
              >
                <span
                  class="i-lucide-alert-triangle size-3.5 shrink-0 mt-0.5"
                />
                <span
                  >Some templates are missing. Go to
                  <strong
                    >Chatwoot → Settings → Inboxes →
                    {{ templateSyncResult.inbox_name }} → Sync Templates</strong
                  >
                  after creating them in Meta.</span
                >
              </div>
            </div>

            <!-- Variables guide -->
            <div
              class="text-xs text-n-slate-8 bg-n-alpha-1 rounded-lg px-3 py-2 border border-n-weak"
            >
              <p class="font-medium text-n-slate-9 mb-1">
                Template variables (positional):
              </p>
              <p>
                <code class="font-mono">&#123;&#123;1&#125;&#125;</code> —
                Customer first name
              </p>
              <p>
                <code class="font-mono">&#123;&#123;2&#125;&#125;</code> —
                Project name
              </p>
            </div>
          </div>
        </section>

        <!-- ── Meeting Confirmation Templates ──────────────────────────── -->
        <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <section
          id="nexceo-meeting-templates"
          class="bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-5 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-video size-3.5 text-n-slate-8 shrink-0" />
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Meeting Confirmation Templates
              </h2>
            </div>
            <button
              :disabled="meetingTemplateLoading"
              class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
              @click="checkMeetingTemplates"
            >
              <span
                v-if="meetingTemplateLoading"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-refresh-cw size-3.5" />
              Check Template Sync
            </button>
          </div>

          <div class="px-5 py-4 space-y-4">
            <p class="text-xs text-n-slate-8 leading-relaxed">
              NEXCEO uses these 4 Meta-approved WhatsApp templates to send
              customer confirmations for site visit and video meeting
              scheduling, rescheduling, and cancellation. Each template uses
              <strong>5 body variables</strong>. Create and approve them in Meta
              Business Manager, then sync in
              <strong
                >Chatwoot → Settings → Inboxes → WhatsApp → Sync
                Templates</strong
              >.
            </p>

            <!-- Template list with variable guide -->
            <div class="space-y-3">
              <div
                v-for="tmpl in [
                  {
                    name: 'nexceo_video_meeting_confirmation',
                    use: 'Video call confirmation',
                    vars: [
                      '{{1}} Customer name',
                      '{{2}} Project / company name',
                      '{{3}} Meeting date & time',
                      '{{4}} Google Meet link',
                      '{{5}} Agent / representative name',
                    ],
                  },
                  {
                    name: 'nexceo_site_visit_confirmation',
                    use: 'Physical site visit confirmation',
                    vars: [
                      '{{1}} Customer name',
                      '{{2}} Project name',
                      '{{3}} Visit date & time',
                      '{{4}} Site / location address',
                      '{{5}} Agent / representative name',
                    ],
                  },
                  {
                    name: 'nexceo_site_visit_rescheduled',
                    use: 'Rescheduled visit / meeting',
                    vars: [
                      '{{1}} Customer name',
                      '{{2}} Project name',
                      '{{3}} New date & time',
                      '{{4}} Meet link or location',
                      '{{5}} Agent / representative name',
                    ],
                  },
                  {
                    name: 'nexceo_site_visit_cancelled',
                    use: 'Cancelled visit / meeting',
                    vars: [
                      '{{1}} Customer name',
                      '{{2}} Project name',
                      '{{3}} Original date & time',
                      '{{4}} Reason / status',
                      '{{5}} Agent / representative name',
                    ],
                  },
                ]"
                :key="tmpl.name"
                class="border border-n-weak rounded-lg overflow-hidden"
              >
                <!-- Header row -->
                <div
                  class="flex items-center justify-between px-3 py-2.5 bg-n-alpha-1"
                >
                  <div>
                    <p class="text-xs font-mono font-semibold text-n-slate-12">
                      {{ tmpl.name }}
                    </p>
                    <p class="text-[11px] text-n-slate-7 mt-0.5">
                      {{ tmpl.use }}
                    </p>
                  </div>

                  <!-- Sync status badges (per inbox) -->
                  <div
                    v-if="meetingTemplateResult && !meetingTemplateResult.error"
                    class="flex flex-wrap gap-1 ml-3 shrink-0"
                  >
                    <template
                      v-for="inbox in meetingTemplateResult.inboxes"
                      :key="inbox.inbox_id"
                    >
                      <span
                        v-if="inbox.templates[tmpl.name]?.found"
                        class="flex items-center gap-1 text-[10px] text-success-700 bg-success-50 border border-success-200 px-2 py-0.5 rounded-full font-medium"
                        :title="`Synced on ${inbox.inbox_name}`"
                      >
                        <span class="i-lucide-check-circle size-3" />
                        {{ inbox.inbox_name }}
                        <span
                          v-if="inbox.templates[tmpl.name].language"
                          class="font-normal text-success-600"
                          >({{ inbox.templates[tmpl.name].language }})</span
                        >
                      </span>
                      <span
                        v-else
                        class="flex items-center gap-1 text-[10px] text-ruby-700 bg-ruby-50 border border-ruby-200 px-2 py-0.5 rounded-full font-medium"
                        :title="`Not synced on ${inbox.inbox_name}`"
                      >
                        <span class="i-lucide-alert-circle size-3" />
                        {{ inbox.inbox_name }}: missing
                      </span>
                    </template>
                  </div>
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template, vue/max-attributes-per-line -->
                  <span v-else class="text-xs text-n-slate-5 ml-3 shrink-0"
                    >–</span
                  >
                </div>

                <!-- Variable list (collapsed by default; always visible here) -->
                <div
                  class="px-3 py-2 border-t border-n-weak/60 flex flex-wrap gap-x-4 gap-y-0.5"
                >
                  <span
                    v-for="v in tmpl.vars"
                    :key="v"
                    class="text-[11px] text-n-slate-8 font-mono"
                    >{{ v }}</span
                  >
                </div>
              </div>
            </div>

            <!-- Check result banner -->
            <div v-if="meetingTemplateResult">
              <div
                v-if="meetingTemplateResult.error"
                class="flex items-start gap-2 text-xs text-ruby-700 bg-ruby-50 border border-ruby-200 rounded-lg px-3 py-2"
              >
                <span class="i-lucide-alert-circle size-3.5 shrink-0 mt-0.5" />
                {{ meetingTemplateResult.error }}
              </div>
              <div
                v-else-if="meetingTemplateResult.all_synced"
                class="flex items-center gap-2 text-xs text-success-700 bg-success-50 border border-success-200 rounded-lg px-3 py-2 font-medium"
              >
                <span class="i-lucide-check-circle size-3.5" />
                All 4 meeting templates found on at least one WhatsApp inbox.
              </div>
              <div
                v-else
                class="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
              >
                <span
                  class="i-lucide-alert-triangle size-3.5 shrink-0 mt-0.5"
                />
                <span>
                  Some templates are not yet synced. Create them in Meta
                  Business Manager with
                  <strong>category: Utility, language: en</strong>, then go to
                  <strong
                    >Chatwoot → Settings → Inboxes → your WhatsApp inbox → Sync
                    Templates</strong
                  >.
                </span>
              </div>
            </div>
          </div>
        </section>
        <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

        <div class="text-center text-[10px] text-n-slate-5 py-1 select-none">
          NEXCEO Settings v21.1
        </div>

        <!-- Bottom save -->
        <div class="flex justify-end pb-4">
          <button
            :disabled="isSaving"
            class="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
            @click="save"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            {{ isSaving ? 'Saving…' : 'Save Settings' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
